import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Form from "@/components/ui/Form";
import ViewWrapper from "@/components/ui/ViewWrapper";
import EmailAffordanceCard from "@/components/EmailAffordanceCard";
import { useToast } from "@/hooks/useToast";
import { useResendCooldown } from "@/hooks/useResendCooldown";
import { SupabaseInstance } from "@/integrations/supabase";
import { Logger } from "@/utils";
import { LoginSchema, LoginType } from "./form";
import { TOAST_MESSAGES } from "./constants";
import { ROUTES_PATH } from "@/router";
import { SUPABASE_ERRORS } from "../../constants/errorCodes";

export default function LoginPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [unconfirmedEmail, setUnconfirmedEmail] = useState<string | null>(null);
  const {
    secondsLeft,
    isCoolingDown,
    start: startCooldown,
  } = useResendCooldown();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginType>({
    resolver: zodResolver(LoginSchema),
  });

  const onSubmit = handleSubmit((data) => {
    setIsLoading(true);
    setUnconfirmedEmail(null);
    Logger.addBreadcrumb({
      category: "auth",
      message: "login_attempt",
      level: "info",
    });
    SupabaseInstance.login(data.email, data.password)
      .then(({ error }) => {
        if (error) {
          Logger.loggerError({
            section: "login_signin_error",
            message: error,
          });
          if (error?.code && SUPABASE_ERRORS.includes(error?.code)) {
            if (error.code === "email_not_confirmed") {
              setUnconfirmedEmail(data.email);
            }
            handleShowToast(
              error.code.toUpperCase() as keyof typeof TOAST_MESSAGES,
            );
            return;
          }
          handleShowToast("GENERIC_ERROR");
          return;
        }
        handleShowToast("SUCCESS");
        navigate(ROUTES_PATH.DASHBOARD);
      })
      .catch((err) => {
        Logger.loggerError({
          section: "login_signin_exception",
          message: err,
        });
        handleShowToast("SERVER_ERROR");
      })
      .finally(() => {
        setIsLoading(false);
      });
  });

  const handleShowToast = useCallback(
    (type: keyof typeof TOAST_MESSAGES) => {
      toast(TOAST_MESSAGES[type]);
    },
    [toast],
  );

  const handleResendConfirmation = useCallback(() => {
    if (!unconfirmedEmail || isCoolingDown || isResending) return;
    setIsResending(true);
    SupabaseInstance.resendConfirmation(unconfirmedEmail)
      .then(({ error }) => {
        if (error) {
          Logger.loggerError({
            section: "resend_confirmation_error",
            message: error,
          });
          if (error.code === "over_email_send_rate_limit") {
            handleShowToast("OVER_EMAIL_SEND_RATE_LIMIT");
            startCooldown();
            return;
          }
          handleShowToast("GENERIC_ERROR");
          return;
        }
        handleShowToast("CONFIRMATION_RESENT");
        startCooldown();
      })
      .catch((err) => {
        Logger.loggerError({
          section: "resend_confirmation_exception",
          message: err,
        });
        handleShowToast("SERVER_ERROR");
      })
      .finally(() => {
        setIsResending(false);
      });
  }, [
    unconfirmedEmail,
    isCoolingDown,
    isResending,
    startCooldown,
    handleShowToast,
  ]);

  return (
    <ViewWrapper>
      <div className="max-w-md w-full mx-auto p-6 bg-white rounded-lg shadow-md border border-gray-200">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-startfly-black mb-2">
            Iniciar Sesión
          </h1>
          <p className="text-startfly-gray">
            Ingresa a tu cuenta para acceder a tus activos
          </p>
        </div>
        <Form.ErrorView
          isVisible={!!(errors.email || errors.password)}
          title={errors.email?.message || errors.password?.message}
        />
        <Form.Container onSubmit={onSubmit}>
          <Form.Field
            {...register("email")}
            label="Correo Electrónico"
            labelHtmlFor="email"
            id="email"
            type="email"
            placeholder="tu@email.com"
            error={errors.hasOwnProperty("email")}
          />
          <Form.Field
            {...register("password")}
            label="Contraseña"
            labelHtmlFor="password"
            id="password"
            type="password"
            placeholder="••••••••"
            error={errors.hasOwnProperty("password")}
          />
          <div className="mt-4 text-right">
            <button
              type="button"
              className=" text-sm text-startfly-gold hover:underline"
              onClick={() => navigate(ROUTES_PATH.FORGOT_PASSWORD)}
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>
          <Form.SubmitButton
            isLoading={isLoading}
            title="Iniciar Sesión"
            loadingText="Ingresando..."
          />
        </Form.Container>
        {unconfirmedEmail && (
          <EmailAffordanceCard
            email={unconfirmedEmail}
            title="Confirma tu correo para continuar"
            helperText="¿No te llegó? Revisa spam o promociones."
            buttonIdleText="Reenviar correo de confirmación"
            isCoolingDown={isCoolingDown}
            isResending={isResending}
            secondsLeft={secondsLeft}
            onResend={handleResendConfirmation}
          />
        )}
        <Form.FooterAction
          title="¿No tienes una cuenta? "
          buttonText="Regístrate"
          onClick={() => navigate(ROUTES_PATH.REGISTER)}
        />
      </div>
    </ViewWrapper>
  );
}
